import React from "react";
import { makeNavLink } from "./shared.jsx";

// UI utility components: FinalCTA, Crumbs, FAQ, VideoEmbed, TestimonialsSlider,
// FitMatcher, ListItem, InteractiveHint, Turnstile, ServicePricing, RichText

// ── RichText — bezpieczny renderer wąskiej allowlisty inline (BEZ dangerouslySetInnerHTML) ──
// Dla pól edytowalnych w CMS, które mogą zawierać proste formatowanie (np. <b>690 PLN</b>,
// linki w stronach prawnych). Dozwolone TYLKO: <b> <strong> <em> <br> <a href="...">.
// Każdy inny znacznik (w tym <script>, atrybuty on*, style) renderuje się jako zwykły TEKST
// — React escapuje, więc edytor nie może wstrzyknąć aktywnego HTML. href ograniczony do
// bezpiecznych schematów; linki http(s) dostają target=_blank + rel=noopener noreferrer.
const RT_TOKEN = /(<\/?(?:b|strong|em)>|<br\s*\/?>|<a\b[^>]*>|<\/a>)/gi;
const RT_SAFE_SCHEME = /^(?:https?:|mailto:|tel:)/i;
const rtSafeHref = (raw) => {
  if (!raw) return null;
  const h = String(raw).trim();
  if (h.startsWith("/") || h.startsWith("#")) return h;   // względne
  return RT_SAFE_SCHEME.test(h) ? h : null;               // odrzuć javascript:, data:, itp.
};
const RichText = ({ text }) => {
  const s = String(text == null ? "" : text);
  if (!s) return null;
  const parts = s.split(RT_TOKEN);
  const root = [];
  const stack = [{ tag: null, href: null, kids: root }];
  let key = 0;
  const top = () => stack[stack.length - 1];
  const collapse = () => {
    const f = stack.pop();
    const k = "rt" + (key++);
    let el;
    if (f.tag === "a") {
      el = f.href
        ? <a key={k} href={f.href} {...(/^https?:/i.test(f.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{f.kids}</a>
        : <React.Fragment key={k}>{f.kids}</React.Fragment>;
    } else {
      el = React.createElement(f.tag, { key: k }, f.kids);
    }
    top().kids.push(el);
  };
  const closeTag = (tag) => {
    if (!stack.some((f) => f.tag === tag)) return;        // stray close → ignoruj
    while (top().tag !== tag) collapse();
    collapse();
  };
  for (const part of parts) {
    if (!part) continue;
    const low = part.toLowerCase();
    if (/^<br\s*\/?>$/.test(low)) { top().kids.push(<br key={"rt" + (key++)} />); continue; }
    if (low === "<b>" || low === "<strong>" || low === "<em>") { stack.push({ tag: low.slice(1, -1), href: null, kids: [] }); continue; }
    if (low === "</b>" || low === "</strong>" || low === "</em>") { closeTag(low.slice(2, -1)); continue; }
    if (/^<a\b/.test(low)) {
      const m = part.match(/href\s*=\s*"([^"]*)"/i) || part.match(/href\s*=\s*'([^']*)'/i);
      stack.push({ tag: "a", href: rtSafeHref(m && m[1]), kids: [] });
      continue;
    }
    if (low === "</a>") { closeTag("a"); continue; }
    top().kids.push(part);                                 // zwykły tekst
  }
  while (stack.length > 1) collapse();
  return <>{root}</>;
};

const FinalCTA = ({ title, sub, navigate, chips }) => {
  const goKontakt = () => {
    if (navigate) return navigate("/kontakt");
    window.history.pushState({}, "", "/kontakt");
    window.dispatchEvent(new PopStateEvent("popstate"));
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  return (
    <section className="final-cta-wrap">
      <div className="final-cta-block">
        <span className="final-eyebrow rv">→ Let's commerce</span>
        <h2 className="final-h rv">
          {title || <>Pierwsza konsultacja<br/>zawsze <em>za darmo.</em></>}
        </h2>
        <p className="final-sub rv">{sub || "30 minut. Jeden problem. Konkretna odpowiedź. Pokaż nam co Cię boli w Twoim e-commerce — audyt konta, tracking, feed, strategia — i wyjdziesz z rozwiązaniem."}</p>
        <div className="final-ctas rv">
          <button className="lc-btn final-btn-dark" onClick={goKontakt}>
            Umów konsultację <span className="lc-btn-arr">↗</span>
          </button>
          <a className="lc-btn final-btn-ghost" href="mailto:hello@letscommerce.pl">
            hello@letscommerce.pl <span className="lc-btn-arr">↗</span>
          </a>
        </div>
        {chips && chips.length > 0 && (
          <div className="final-chips rv">
            {chips.map((c, i) => <span key={i} className="final-chip">{c}</span>)}
          </div>
        )}
      </div>
    </section>
  );
};


// SITE_URL hardcoded here too — schema.org URLs MUST be absolute. Same value as in shared.jsx useMeta.
const CRUMBS_SITE_URL = "https://letscommerce.pl";

const Crumbs = ({ items, navigate }) => {
  const link = makeNavLink(navigate);
  // Inject BreadcrumbList JSON-LD into <head> so search engines understand
  // hierarchy (improves SERP appearance — breadcrumb trail under page title).
  React.useEffect(() => {
    if (!items || items.length === 0) return;
    const id = "lc-breadcrumb-jsonld";
    const data = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.label,
        ...(it.href ? { item: CRUMBS_SITE_URL + (it.href === "/" ? "/" : it.href.replace(/\/$/, "")) } : {}),
      })),
    };
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { const e = document.getElementById(id); if (e) e.remove(); };
  }, [items]);

  return (
    <div className="breadcrumbs">
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="sep">›</span>}
          {it.href ? <a {...link(it.href)}>{it.label}</a> : <span className="curr">{it.label}</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

const FAQ = ({ items }) => {
  const [open, setOpen] = React.useState(0);
  return (
    <div className="faq">
      {items.map((it, i) => {
        const paras = Array.isArray(it.a) ? it.a : [it.a];
        const isOpen = open === i;
        const panelId = `faq-${i}`;
        return (
          <div key={i} className={"faq-item" + (isOpen ? " open" : "")}>
            <button
              type="button"
              className="faq-q-btn"
              onClick={() => setOpen(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              aria-controls={panelId}
            >
              <h3 className="faq-q">{it.q}<span className="ico" aria-hidden="true">+</span></h3>
            </button>
            <div className="faq-a" id={panelId} role="region">
              {paras.map((p, j) => <p key={j}>{p}</p>)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const VideoEmbed = ({ videoId = "QH94SbDtxyM" }) => {
  const [playing, setPlaying] = React.useState(false);
  return (
    <div>
      <div className="video-wrap" onClick={() => setPlaying(true)} role="button" aria-label="Odtwórz wideo referencji">
        {playing ? (
          <iframe
            className="video-iframe"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title="Referencja"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <>
            <img
              className="video-thumb-img" loading="lazy" decoding="async"
              src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
              alt="Referencja"
              onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`; }}
            />
            <div style={{position:"absolute",inset:0,background:"linear-gradient(180deg,rgba(10,10,10,.1),rgba(10,10,10,.55))",pointerEvents:"none"}}/>
            <div className="video-play">▶</div>
          </>
        )}
      </div>
    </div>
  );
};

const TestimonialsSlider = ({ items }) => {
  const [idx, setIdx] = React.useState(0);
  const [playingVideo, setPlayingVideo] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [viewportH, setViewportH] = React.useState(null);
  const touchStart = React.useRef(null);
  const slideRefs = React.useRef([]);
  const total = items.length;

  const goTo = (i) => {
    setPlayingVideo(false);
    setIdx((i + total) % total);
  };
  const next = () => goTo(idx + 1);
  const prev = () => goTo(idx - 1);

  React.useEffect(() => {
    if (hovered || playingVideo) return;
    const t = setInterval(() => setIdx(i => (i + 1) % total), 2000);
    return () => clearInterval(t);
  }, [hovered, playingVideo, total]);

  // Match viewport height to active slide so quote slides don't stretch video slides.
  React.useLayoutEffect(() => {
    const measure = () => {
      const el = slideRefs.current[idx];
      if (el) setViewportH(el.offsetHeight);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    slideRefs.current.forEach(el => el && ro && ro.observe(el));
    window.addEventListener("resize", measure);
    return () => { ro && ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [idx, total, playingVideo]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next() : prev());
    touchStart.current = null;
  };

  return (
    <div className="tslider" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div
        className="tslider-viewport"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={viewportH ? { height: viewportH } : undefined}
      >
        <div className="tslider-track" style={{ transform: `translate3d(-${idx * 100}%, 0, 0)` }}>
          {items.map((it, i) => (
            <div key={i} ref={el => (slideRefs.current[i] = el)} className={"tslider-slide" + (it.type === "quote" ? " is-quote" : " is-video")}>
              {it.type === "video" ? (
                <div
                  className="tslide-video"
                  onClick={() => i === idx && setPlayingVideo(true)}
                  role="button"
                  aria-label="Odtwórz wideo referencji"
                >
                  {playingVideo && i === idx ? (
                    <iframe
                      className="video-iframe"
                      src={`https://www.youtube.com/embed/${it.videoId}?autoplay=1&rel=0`}
                      title="Referencja wideo"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img
                        className="video-thumb-img" loading="lazy" decoding="async"
                        src={`https://i.ytimg.com/vi/${it.videoId}/maxresdefault.jpg`}
                        alt="Referencja wideo"
                        onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${it.videoId}/hqdefault.jpg`; }}
                      />
                      <div style={{position:"absolute",inset:0,background:"linear-gradient(180deg,rgba(10,10,10,.1),rgba(10,10,10,.55))",pointerEvents:"none"}}/>
                      <div className="video-play">▶</div>
                    </>
                  )}
                </div>
              ) : (
                <figure className="tslide-quote">
                  <span className="qmark" aria-hidden="true">"</span>
                  <blockquote>{it.quote}</blockquote>
                  <figcaption className="qattr">
                    <span className="qattr-name">{it.name}</span>
                    {it.org && <span className="qattr-co">{it.org}</span>}
                  </figcaption>
                </figure>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="tslider-controls">
        <button className="tslider-arrow" onClick={prev} aria-label="Poprzednia referencja">‹</button>
        <div className="tslider-dots" role="tablist">
          {items.map((_, i) => (
            <button
              key={i}
              className={"tslider-dot" + (i === idx ? " active" : "")}
              onClick={() => goTo(i)}
              aria-label={`Referencja ${i + 1} z ${total}`}
              aria-selected={i === idx}
              role="tab"
            />
          ))}
        </div>
        <button className="tslider-arrow" onClick={next} aria-label="Następna referencja">›</button>
      </div>
    </div>
  );
};

const FitMatcher = ({ good, bad, eyebrow = "Dopasowanie", title, goodTitle = "To jest dla Ciebie, jeśli...", badTitle = "To NIE jest dla Ciebie, jeśli..." }) => (
  <section className="sec container">
    <div className="sec-head rv">
      <span className="eyebrow">{eyebrow}</span>
      {title && <h2 className="h2">{title}</h2>}
    </div>
    <div className="fit-grid">
      <div className="fit-card good rv">
        <h3>{goodTitle}</h3>
        <ul>{good.map((x, i) => <li key={i}>{x}</li>)}</ul>
      </div>
      <div className="fit-card bad rv">
        <h3>{badTitle}</h3>
        <ul>{bad.map((x, i) => <li key={i}>{x}</li>)}</ul>
      </div>
    </div>
  </section>
);

const ListItem = ({ icon = "✓", title, body, meta, className = "" }) => (
  <li className={`check-item rv${className ? " " + className : ""}`}>
    <span className="check-ico">{icon}</span>
    <div>
      {title && <h3 className="check-ttl">{title}</h3>}
      {body && <p className="check-body">{body}</p>}
    </div>
    {meta && <span className="check-meta">{meta}</span>}
  </li>
);

const ListNum = ({ i, total }) => (
  <span className="check-meta-num"><em>{String(i+1).padStart(2,"0")}</em> / {String(total).padStart(2,"0")}</span>
);
const ListArrow = () => <span className="check-meta-arrow">↗</span>;

const InteractiveHint = ({ note, caption, position = "side", children }) => {
  const cls = "ih-wrap ih-" + (position === "below" ? "below" : "side");
  return (
    <div className={cls}>
      <div className="ih-grid">
        <div className="ih-content">{children}</div>
        {note && (
          <aside className="ih-note">
            {position === "below" ? (
              <svg className="ih-arrow ih-arrow-below" viewBox="0 0 36 56" width="36" height="56" fill="none" aria-hidden="true">
                <path d="M28 50 C28 36 12 28 14 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="3.5 4" fill="none"/>
                <path d="M9 16 L14 8 L20 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            ) : (
              <svg className="ih-arrow ih-arrow-side" viewBox="0 0 70 80" width="64" height="74" fill="none" aria-hidden="true">
                <path d="M55 70 C55 42 38 18 12 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="3.5 4" fill="none"/>
                <path d="M19 5 L12 8 L15 15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            )}
            <span className="ih-tag">NOTE</span>
            <p className="ih-text">{note}</p>
          </aside>
        )}
      </div>
      {caption && <p className="ih-caption">{caption}</p>}
    </div>
  );
};

// Cloudflare Turnstile — privacy-first CAPTCHA, no cookies, GDPR-friendly.
// SITE KEY below is Cloudflare's public test key (always passes). Swap for production
// key from dash.cloudflare.com → Turnstile when going live. Pair with TURNSTILE_SECRET_KEY
// (env var, validated server-side in Netlify Functions).
const TURNSTILE_SITE_KEY = "0x4AAAAAADLqPZc8tiFdpxpl";
const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js";

const Turnstile = ({ onToken, theme = "dark" }) => {
  const ref = React.useRef(null);
  const widgetIdRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !ref.current || !window.turnstile) return;
      try {
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme,
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      } catch (e) { /* widget may already be rendered if React strict-mode double-mount */ }
    };
    if (window.turnstile && window.turnstile.render) {
      render();
    } else if (!document.querySelector(`script[src^="${TURNSTILE_SCRIPT}"]`)) {
      const s = document.createElement("script");
      s.src = TURNSTILE_SCRIPT;
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      // Script tag exists but turnstile not ready yet — poll briefly
      const t = setInterval(() => {
        if (window.turnstile && window.turnstile.render) {
          clearInterval(t);
          render();
        }
      }, 100);
      setTimeout(() => clearInterval(t), 5000);
    }
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
      }
    };
  }, []);

  return <div ref={ref} className="turnstile-widget" aria-label="Weryfikacja antybotowa" />;
};

// Standardized pricing card for service subpages — single source of truth so
// /uslugi/feed, /uslugi/capi, /uslugi/analityka etc. all render identically.
//
// Usage:
//   <ServicePricing
//     costType="KOSZT JEDNORAZOWY"      // or "KOSZT MIESIĘCZNY"
//     pricePrefix="setup od"            // or "od" or ""
//     priceValue="2 500"                // visible big green number
//     currency="PLN"                    // or "PLN /msc" etc.
//     subtitle="audyt standalone od <b>690 PLN</b>..."   // HTML, optional
//     includes={["item 1", "item 2", ...]}               // green-tinted box
//     factors={[{t: "Liczba SKU", b: "..."}, ...]}       // right column
//   />
const ServicePricing = ({
  costType = "KOSZT JEDNORAZOWY",
  pricePrefix = "od",
  priceValue,
  currency = "PLN",
  subtitle,
  includesTitle = "// CO DOSTAJESZ W TEJ CENIE",
  includes = [],
  factorsTitle = "CO WPŁYWA NA WYCENĘ",
  factors = [],
}) => (
  <section className="sec container">
    <div className="sec-head rv">
      <span className="eyebrow">// CENA</span>
      <h2 className="h2">Ile to <em>kosztuje.</em></h2>
    </div>
    <div className="rv" style={{ marginTop: 40 }}>
      <div className="svc-price-card lc-2col" style={{ gap: 32 }}>
        <div>
          <div className="svc-price-eyebrow">{costType}</div>
          <div className="svc-price-num">
            {pricePrefix && <>{pricePrefix} </>}
            <span className="accent">{priceValue}</span>
            <span className="unit"> {currency}</span>
          </div>
          {subtitle && <div className="svc-price-subtitle"><RichText text={subtitle} /></div>}
          {includes.length > 0 && (
            <div className="svc-price-includes">
              <div className="svc-price-includes-title">{includesTitle}</div>
              {includes.map((it, i) => (
                <div key={i} className="svc-price-includes-row">
                  <span className="check">✓</span>
                  <span className="text"><RichText text={it} /></span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="svc-price-eyebrow">{factorsTitle}</div>
          <ul className="svc-price-factors">
            {factors.map((f, i) => (
              <li key={i} className={i === factors.length - 1 ? "last" : ""}>
                <div className="t">{f.t}</div>
                <div className="b">{f.b}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export { FinalCTA, Crumbs, FAQ, VideoEmbed, TestimonialsSlider, FitMatcher, ListItem, ListNum, ListArrow, InteractiveHint, Turnstile, ServicePricing, RichText };
